import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Breadcrumbs as MuiBreadcrumbs, 
  Typography, 
  Link as MuiLink, 
  Box,
  useTheme,
  alpha
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const theme = useTheme();

  return (
    <Box 
      sx={{ 
        mb: 3, 
        display: 'flex', 
        alignItems: 'center' 
      }}
    >
      <MuiBreadcrumbs 
        separator={
          <NavigateNextIcon 
            fontSize="small" 
            sx={{ 
              color: alpha(theme.palette.text.primary, 0.4),
              fontSize: '1rem' 
            }} 
          />
        }
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center'
          }
        }}
      >
        {/* Home breadcrumb always present */}
        <MuiLink
          component={Link}
          to="/"
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: theme.palette.text.secondary,
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main,
              textDecoration: 'none'
            }
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          Home
        </MuiLink>
        
        {/* Additional breadcrumbs */}
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          
          return isLast ? (
            // Current page (no link)
            <Typography
              key={item.label}
              sx={{ 
                display: 'flex',
                alignItems: 'center', 
                color: theme.palette.text.primary,
                fontWeight: 600
              }}
            >
              {item.icon && (
                <Box component="span" sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </Box>
              )}
              {item.label}
            </Typography>
          ) : (
            // Clickable breadcrumb
            <MuiLink
              key={item.label}
              component={Link}
              to={item.path || '#'}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.text.secondary,
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.primary.main,
                  textDecoration: 'none'
                }
              }}
            >
              {item.icon && (
                <Box component="span" sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </Box>
              )}
              {item.label}
            </MuiLink>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumb;